import { MealTagsScrolling } from '@tovala/component-library'
import {
  MealImage,
  MealTag,
  NutritionalInfoItem,
} from '@tovala/browser-apis-combinedapi'
import { ReactNode } from 'react'
import { partition } from 'lodash-es'
import { getDisplayTags } from './utils'

import { events } from 'analytics/events'
import { TAG_IDS } from 'utils/meals'
import { track } from 'utils/analytics'

import {
  Allergens,
  ChefImage,
  ComponentImages,
  DetailsCollapsible,
  Ingredients,
  MainImage,
  Nutrition,
  PreparationSteps,
  SectionID,
} from './shared'

export const MealDetails = ({
  expirationDate,
  cookTime,
  imageTag,
  meal,
  price,
  review,
  stepper,
  title,
}: {
  expirationDate?: ReactNode
  cookTime?: ReactNode
  imageTag?: ReactNode
  meal: {
    chefImage: string
    createdBy: string
    id: number
    images: MealImage[]
    ingredients: string
    mealPrepSteps: string
    nutritionalInfo: NutritionalInfoItem[]
    story: string
    subtitle: string
    tags: MealTag[]
  }
  price?: ReactNode
  review?: ReactNode
  stepper?: ReactNode
  title: ReactNode
}) => {
  const [mainImage, componentImages] = partition(
    meal.images,
    (image) => image.key === 'cell_tile',
  )

  const onCollapsibleClick = (sectionID: SectionID) => {
    track(events.OPENS_MEAL_DETAIL_DISCLOSURE_TAB, {
      meal_detail_section_id: sectionID,
    })
  }

  return (
    <>
      <div className="mx-4 my-8">
        <div className="text-k/28_110">{title}</div>
        <p className="text-k/24_120 text-grey-8">{meal.subtitle}</p>
      </div>

      <MainImage
        chefImage={
          meal.chefImage ? (
            <ChefImage chefImage={meal.chefImage} createdBy={meal.createdBy} />
          ) : null
        }
        imageTag={imageTag}
        imageURL={mainImage[0]?.url}
        stepper={stepper}
      />

      {price && (
        <div className="mx-4 mt-4">
          <p className="text-k/14_120 font-medium">{price}</p>
        </div>
      )}

      {(cookTime || meal.tags.length > 0) && (
        <div className="my-4 ml-4 flex items-center">
          {cookTime}

          {meal.tags.length > 0 && (
            <div className="overflow-hidden border-l border-grey-4 pl-4 first:border-l-0 first:pl-0">
              <MealTagsScrolling tags={getDisplayTags({ tags: meal.tags })} />
            </div>
          )}
        </div>
      )}

      <div className="mx-4 mb-32 mt-4">
        <h3 className="mb-4 text-k/20_110 md:mb-3">Description</h3>
        {meal.tags.find((tag) => tag.id === TAG_IDS.FAMILY) && (
          <FamilyMealDisclaimer />
        )}
        <p className="mb-4 text-k/14_120 tracking-normal text-grey-9 md:mb-3">
          {meal.story}
        </p>

        {expirationDate}

        {review}

        <div className="divide-y divide-grey-4 border-b border-t border-grey-4">
          {meal.nutritionalInfo && (
            <div>
              <DetailsCollapsible
                onCollapsibleClick={onCollapsibleClick}
                sectionID="nutrition"
                title="Nutrition"
              >
                <div>
                  <Nutrition nutritionalInfo={meal.nutritionalInfo} />
                </div>
              </DetailsCollapsible>
            </div>
          )}

          {componentImages.length > 0 && (
            <div>
              <DetailsCollapsible
                onCollapsibleClick={onCollapsibleClick}
                sectionID="in_the_box"
                title="In the Box"
              >
                <div>
                  <ComponentImages
                    images={componentImages.map(({ caption, url }) => ({
                      caption,
                      url,
                    }))}
                  />
                </div>
              </DetailsCollapsible>
            </div>
          )}

          {meal.ingredients && (
            <div>
              <DetailsCollapsible
                onCollapsibleClick={onCollapsibleClick}
                sectionID="ingredients"
                title="Ingredients"
              >
                <Ingredients ingredients={meal.ingredients} />
              </DetailsCollapsible>
            </div>
          )}

          <div>
            <DetailsCollapsible
              onCollapsibleClick={onCollapsibleClick}
              sectionID="allergens"
              title="Allergens"
            >
              <Allergens
                allergens={
                  meal.nutritionalInfo
                    ?.filter(({ key }) => key === 'allergen')
                    .map((allergen) => allergen.name) || []
                }
              />
            </DetailsCollapsible>
          </div>

          {meal.mealPrepSteps && (
            <div>
              <DetailsCollapsible
                onCollapsibleClick={onCollapsibleClick}
                sectionID="prep_steps"
                title="Preparation Steps"
              >
                <PreparationSteps steps={splitPrepSteps(meal.mealPrepSteps)} />
              </DetailsCollapsible>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

const FamilyMealDisclaimer = () => {
  return (
    <p className="mb-4 text-k/14_120 tracking-normal text-grey-9">
      <span className="font-semibold">Family Meal:</span> Serves 4. All servings
      cook together, no single servings. Uses your air-fry basket and black
      sheet tray (included with your oven). Nutrition shown per serving.{' '}
      <span className="font-semibold">Questions about Family Meals? </span>
      <a
        className="underline"
        href="https://www.tovala.com/familymealsFAQ"
        rel="noreferrer noopener"
        target="_blank"
      >
        Learn more
      </a>
    </p>
  )
}

function splitPrepSteps(steps: string) {
  return steps.split(/[0-9]+\./g).filter((s) => s)
}
